import { Combobox, Transition } from '@headlessui/react'
import { ChevronUpDownIcon } from '@heroicons/react/20/solid'
import clsx from 'clsx'
import { Fragment, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'

import { useChangeUserCompanyMutation, useGetUserCompaniesQuery } from '../../../app/apiSlice'
import { InlineLoader } from '../../../common/components'
import { useDashboard } from '../../../common/hooks'
import { MainPathEnum, SubPathEnum } from '../../../common/types/routing'

interface CompanyOption {
  label: string
  value: string
  companyId: number
}

const CompanySelect = () => {
  const location = useLocation()
  const { company } = useDashboard()

  const { data } = useGetUserCompaniesQuery()
  const [changeCompany] = useChangeUserCompanyMutation()

  const [isSwitchingCompany, setIsSwitchingCompany] = useState<boolean>(false)
  const [selectedCompanyId, setSelectedCompanyId] = useState<string>(company.id)
  const [query, setQuery] = useState<string>('')
  const inputRef = useRef<HTMLInputElement | null>(null)

  useEffect(() => {
    if (!isSwitchingCompany) setSelectedCompanyId(company.id)
  }, [company.id, isSwitchingCompany])

  const options = useMemo<CompanyOption[]>(
    () =>
      (data ?? []).map((c) => ({
        label: c.company_name,
        value: c.company_uuid,
        companyId: c.company_id,
      })),
    [data],
  )

  const selectedOption =
    options.find((option) => option.value === selectedCompanyId) ??
    options.find((option) => option.value === company.id) ??
    null

  const normalizedQuery = query.trim().toLowerCase()
  const filteredOptions =
    normalizedQuery.length === 0
      ? options
      : options.filter((option) => option.label.toLowerCase().includes(normalizedQuery))

  const isCompaniesLoaded = Array.isArray(data)
  const hasSingleCompany = isCompaniesLoaded && options.length === 1

  const onChangeCompany = async (option: CompanyOption): Promise<void> => {
    if (option.value === selectedCompanyId || isSwitchingCompany) return

    inputRef.current?.blur()
    setIsSwitchingCompany(true)
    setSelectedCompanyId(option.value)
    setQuery('')
    const pathFragments = location.pathname.replace(/^\/+/, '').split('/')
    const [rootSegment, secondSegment] = pathFragments
    const isOpenedProcessView =
      rootSegment === MainPathEnum.PROCESSES &&
      secondSegment !== undefined &&
      secondSegment !== SubPathEnum.TASK_DISCOVERY
    const redirectPath = isOpenedProcessView ? `/${MainPathEnum.PROCESSES}` : location.pathname

    try {
      await changeCompany(option.companyId).unwrap()
      window.location.assign(redirectPath)
    } catch {
      setIsSwitchingCompany(false)
      setSelectedCompanyId(company.id)
    }
  }

  if (hasSingleCompany) {
    return (
      <div className='w-80 max-w-full'>
        <p className='truncate text-sm font-semibold text-slate-900'>
          {selectedOption?.label ?? company.name}
        </p>
      </div>
    )
  }

  if (isSwitchingCompany) {
    return (
      <div className='w-80 max-w-full rounded-xl border border-slate-300/90 bg-slate-50 px-3.5 py-2.5 shadow-sm'>
        <div className='flex items-center justify-between gap-2'>
          <span className='min-w-0 truncate text-sm font-semibold text-slate-900'>
            {selectedOption?.label ?? company.name}
          </span>
          <InlineLoader size='btn_xs' />
        </div>
      </div>
    )
  }

  return (
    <Combobox
      value={selectedOption}
      onChange={(option: CompanyOption) => void onChangeCompany(option)}
      disabled={isSwitchingCompany}
    >
      <div className='relative w-80 max-w-full'>
        <div className='relative w-full cursor-default overflow-hidden rounded-xl border border-slate-300/90 bg-white text-left text-sm shadow-sm transition focus-within:border-primary-500 focus-within:ring-1 focus-within:ring-primary-500'>
          <Combobox.Input
            ref={inputRef}
            className='w-full border-none bg-transparent py-2.5 pl-3.5 pr-10 text-sm font-semibold text-slate-900 focus:outline-none disabled:cursor-not-allowed disabled:bg-slate-50 disabled:text-slate-500'
            displayValue={(option: CompanyOption) => option?.label ?? ''}
            onChange={(event) => setQuery(event.target.value)}
            placeholder='Search companies...'
          />
          <Combobox.Button className='absolute inset-y-0 right-0 flex items-center pr-2.5 disabled:cursor-not-allowed'>
            {isSwitchingCompany ? (
              <InlineLoader size='btn_xs' />
            ) : (
              <ChevronUpDownIcon className='size-5 text-slate-600' />
            )}
          </Combobox.Button>
        </div>
        {!isSwitchingCompany ? (
          <Transition
            as={Fragment}
            leave='transition ease-in duration-100'
            leaveFrom='opacity-100'
            leaveTo='opacity-0'
            afterLeave={() => setQuery('')}
          >
            <Combobox.Options className='absolute z-50 mt-1 max-h-96 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black/5'>
              {filteredOptions.length === 0 ? (
                <li className='px-3 py-2 text-slate-500'>No companies found</li>
              ) : (
                filteredOptions.map((option) => (
                  <Combobox.Option
                    key={option.value}
                    value={option}
                    className={({ active }) =>
                      clsx(
                        'relative cursor-pointer select-none px-3 py-2',
                        active ? 'bg-primary-600 text-white' : 'text-gray-900',
                      )
                    }
                  >
                    {({ selected, active }) => (
                      <span className='flex items-center gap-2'>
                        <span
                          className={clsx('min-w-0 flex-1 truncate', selected && 'font-semibold')}
                        >
                          {option.label}
                        </span>
                        {option.value === selectedCompanyId ? (
                          <span
                            className={clsx(
                              'shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide',
                              active
                                ? 'bg-white/20 text-white'
                                : 'bg-primary-50 text-primary-700 ring-1 ring-primary-200',
                            )}
                          >
                            Current
                          </span>
                        ) : null}
                      </span>
                    )}
                  </Combobox.Option>
                ))
              )}
            </Combobox.Options>
          </Transition>
        ) : null}
      </div>
    </Combobox>
  )
}

export default CompanySelect

